import { Link, useParams } from 'react-router-dom';

/**
 * No page at this address.
 *
 * Inside a workspace (an old link to a renamed page, a typo after
 * /o/your-org/) it renders in the shell, so the header, the rail and the
 * bottom bar stay and the way back is where it always is. It used to sit
 * outside the workspace's routes and drop the whole shell. Outside a
 * workspace it is a page of its own under the green-ink band, like every
 * other screen of the product, rather than a white header.
 */
export function NotFoundPage({ inShell = false }: { inShell?: boolean }) {
  const { orgSlug } = useParams();

  const body = (
    <>
      <p className="record text-xs text-ink-3">404</p>
      <h1 className="mt-2 font-display text-2xl font-semibold leading-tight text-ink">
        There is no page at this address.
      </h1>
      <p className="mt-2 max-w-[60ch] text-sm text-ink-2">
        The link may be old, or the workspace address changed after a rename. Your workspace and its
        calendar are where they were.
      </p>
      <p className="mt-6 flex flex-wrap items-center gap-x-2 text-sm">
        {inShell && orgSlug ? (
          <Link to={`/o/${orgSlug}`} className="inline-flex min-h-tap items-center font-semibold text-forest underline underline-offset-2">
            Go to the workspace home
          </Link>
        ) : (
          <>
            <Link to="/" className="inline-flex min-h-tap items-center font-semibold text-forest underline underline-offset-2">
              Go to my workspace
            </Link>
            <span className="text-ink-3">or</span>
            <Link to="/demo" className="inline-flex min-h-tap items-center font-semibold text-forest underline underline-offset-2">
              open the demo
            </Link>
          </>
        )}
      </p>
    </>
  );

  if (inShell) return <div className="py-6">{body}</div>;

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-white/10 bg-forest-ink pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-6xl items-center px-4 py-2 md:py-3">
          <a
            href="https://claripick.com"
            className="-ml-2 flex min-h-tap items-center gap-2 rounded-ct-sm px-2"
            aria-label="Back to claripick.com"
          >
            <img src="/logo.svg" alt="" width={28} height={28} className="h-7 w-7 max-w-none brightness-0 invert" />
            <span className="font-display text-lg font-semibold tracking-[-0.02em] text-white">Claripick</span>
          </a>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-12 sm:py-16">{body}</main>
    </div>
  );
}
